import {Injectable} from "@angular/core";
import {CalendarView} from "@progress/kendo-angular-dateinputs";
import * as moment from "moment";
import {BehaviorSubject, Observable} from "rxjs";
import {HijriDateProxy} from "./hijri.date.proxy";
import * as _ from "lodash";
import {subscribe} from "diagnostics_channel";

export interface HijriDate {
	day: string;
	month: string;
	year: string;
}

@Injectable({ providedIn: 'root' })
  export class HijriDateService {

	private hijriDatesSubject: BehaviorSubject<{ [date: string]: HijriDate }> = new BehaviorSubject<{ [date: string]: HijriDate }>({});

	public hijriDatesObservable: Observable<{ [date: string]: HijriDate }>;

	public debounceGetHijriDatesInMonth;
	public debounceGetHijriDatesInYear;
	public debounceGetHijriDatesInDecade;

	constructor(private hijriDateProxy: HijriDateProxy) {
		this.hijriDatesObservable = this.hijriDatesSubject.asObservable();
		this.debounceGetHijriDatesInMonth = _.debounce((date) => this.getHijriDatesInMonth(date), 200);
		this.debounceGetHijriDatesInYear = _.debounce((date) => this.getHijriDatesInYear(date), 200);
		this.debounceGetHijriDatesInDecade = _.debounce((date) => this.getHijriDatesInDecade(date), 200);

	}

	public getAllHijriDates(startDate: Date, endDate: Date): void {
		const startDateMoment = moment(startDate);
		//const endDateMoment = moment(endDate);

		this.hijriDateProxy.getAllHijriDates(startDate, endDate).pipe().subscribe(res => {

			let hijriDates = {...this.hijriDatesSubject.getValue()};

			let index = 0;

			const year = moment(startDate).year();
			const startOfDecadeYear = Math.trunc(year / 10) * 10;

			let startOfYear = moment(new Date(startOfDecadeYear, 0, 1));
			let endOfYear = moment(new Date(startOfDecadeYear, 11, 31));

			for (; index < 20; index = index + 2) {
				hijriDates[startOfYear.valueOf()] = res[index];
				hijriDates[endOfYear.valueOf()] = res[index + 1];
				startOfYear = moment(startOfYear).add(1, "year");
				endOfYear = moment(endOfYear).add(1, "year");
			}

			let startOfMonth = moment(startDate).startOf("year");
			let endOfMonth = moment(startOfMonth).endOf("month").startOf("day");

			for (; index < 44; index = index + 2) {
				hijriDates[startOfMonth.valueOf()] = res[index];
				hijriDates[endOfMonth.valueOf()] = res[index + 1];
				startOfMonth = moment(startOfMonth).add(1, "month").startOf("month");
				endOfMonth = moment(startOfMonth).endOf("month").startOf("day");
			}

			let i = startDateMoment;
			for (; index < res.length; index++) {
				hijriDates[i.valueOf()] = res[index];
				i = moment(i).add(1, "day");
			}

			this.hijriDatesSubject.next(hijriDates);
		});

	}

	public getHijriDatesInDecade(date: Date): void {
		const year = moment(date).year();
		const startOfDecadeYear = Math.trunc(year / 10) * 10;

		let startOfYear = moment(new Date(startOfDecadeYear, 0, 1));
		let endOfYear = moment(new Date(startOfDecadeYear, 11, 31));

		this.hijriDateProxy.getHijriDatesInDecade(year).pipe().subscribe((res) => {
			let hijriDates = {...this.hijriDatesSubject.getValue()};
			for (let index = 0; index < 20; index = index + 2) {
				hijriDates[startOfYear.valueOf()] = res[index];
				hijriDates[endOfYear.valueOf()] = res[index + 1];
				startOfYear = moment(startOfYear).add(1, "year");
				endOfYear = moment(endOfYear).add(1, "year");
			}
			this.hijriDatesSubject.next(hijriDates);
		});
	}

	public getHijriDatesInYear(date: Date): void {
		const year = date.getFullYear();
		let startOfMonth = moment(date).startOf("year");
		let endOfMonth = moment(startOfMonth).endOf("month").startOf("day");

		this.hijriDateProxy.getHijriDatesInYear(year).pipe().subscribe((res) => {
			let hijriDates = {...this.hijriDatesSubject.getValue()};
			for (let index = 0; index < res.length; index = index + 2) {
				hijriDates[startOfMonth.valueOf()] = res[index];
				hijriDates[endOfMonth.valueOf()] = res[index + 1];
				startOfMonth = moment(startOfMonth).add(1, "month").startOf("month");
				endOfMonth = moment(startOfMonth).endOf("month").startOf("day");
			}
			this.hijriDatesSubject.next(hijriDates);
		});

	}

	public getHijriDatesInMonth(date: Date) {

		const startOfMonth = moment(date).startOf("month");
		const endOfMonth = moment(date).add(1, "month").endOf("month").startOf("day");
		let getData = false;
		for (let start = startOfMonth; start < endOfMonth; start = moment(start).add(1, "day")) {
			if (!this.hijriDatesSubject[start.valueOf()]) {
				getData = true;
				break;
			}
		}
		if (getData) {

			this.hijriDateProxy.getHijriDatesInMonth(startOfMonth.toDate(), endOfMonth.toDate()).pipe().subscribe(res => {
				let i = startOfMonth;
				let hijriDates = {...this.hijriDatesSubject.getValue()};
				for (let index = 0; index < res.length; index++) {
					hijriDates[i.valueOf()] = res[index];
					i = moment(i).add(1, "day");
				}
				this.hijriDatesSubject.next(hijriDates);
			});

		}
	}

	public getHijriDate(date: Date): void {
		const dateMoment = moment(date).startOf("day");
		this.hijriDateProxy.getHijriDate(date).pipe().subscribe(res => {
			let hijriDates = {...this.hijriDatesSubject.getValue()};
			hijriDates[dateMoment.valueOf()] = res;
			this.hijriDatesSubject.next(hijriDates);
		});
	}

	public formatHijriDate(date: HijriDate, format?: string): string {
		if (date) {
			switch (format) {
				case "iD":
					return date.day;
				case "iMMMM":
					return date.month;
				case "iYYYY":
					return date.year;
				case "iMMMM iYYYY":
					return date.month + " " + date.year;
				case "iD iMMMM":
					return date.day + " " + date.month;
				case "iD iMMMM, iYYYY":
					return date.day + " " + date.month + ", " + date.year;
				default:
					return date.day + " " + date.month + ", " + date.year;
			}
		}
	}
}
